import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useStore } from '../../context/StoreContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Palette, TextT, Image, CircleNotch, FloppyDisk, Upload, Trash, Eye,
  Clock, Warning, User, Plus, X as XIcon
} from '@phosphor-icons/react';
import { Switch } from '../../components/ui/switch';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ColorInput = ({ label, value, onChange, testId }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <div className="flex items-center gap-2 mt-1">
      <input
        type="color"
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-10 h-10 rounded-lg border cursor-pointer"
        data-testid={testId}
      />
      <Input
        value={value}
        onChange={e => onChange(e.target.value)}
        className="font-mono text-sm"
        placeholder="#000000"
      />
    </div>
  </div>
);

const ImageUpload = ({ label, value, onChange, testId }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_URL}/api/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      onChange(res.data.url);
      toast.success('Image uploadée');
    } catch (e) {
      toast.error('Erreur upload');
    } finally { setUploading(false); }
  };

  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="mt-1 border-2 border-dashed rounded-lg p-4 text-center" data-testid={testId}>
        {value ? (
          <div className="relative group">
            <img
              src={value.startsWith('/uploads') ? `${API_URL}${value}` : value}
              alt={label}
              className="max-h-20 mx-auto object-contain"
            />
            <button
              onClick={() => onChange('')}
              className="absolute top-0 right-0 w-6 h-6 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
            >
              <Trash size={12} />
            </button>
          </div>
        ) : (
          <button
            onClick={() => fileRef.current?.click()}
            className="text-slate-400 hover:text-slate-600 transition-colors"
            disabled={uploading}
          >
            {uploading ? <CircleNotch className="animate-spin mx-auto" size={24} /> : <Upload size={24} className="mx-auto" />}
            <p className="text-xs mt-1">{uploading ? 'Upload...' : 'Cliquer pour uploader'}</p>
          </button>
        )}
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} />
      </div>
    </div>
  );
};

const IdentityPage = () => {
  const { currentStore } = useStore();
  const [identity, setIdentity] = useState({
    primary_color: '#0052FF',
    secondary_color: '#1a1a2e',
    accent_color: '#f59e0b',
    text_color: '#1a1a1a',
    background_color: '#ffffff',
    heading_font: 'Inter',
    body_font: 'Inter',
    google_fonts_url: '',
    logo_light: '',
    logo_dark: '',
    favicon: '',
    site_icon: '',
    theme_template: '',
    hero_video_id: '',
    hero_line1: '',
    hero_line2: '',
    hero_subtitle: '',
    hero_cta_text: '',
    shop_video_id: '',
    contact_phone: '',
    contact_email: '',
    contact_address: '',
    company_name: '',
    social_facebook: '',
    social_instagram: '',
    social_tiktok: '',
    google_maps_url: '',
    siret: '',
    tva_number: '',
    footer_text: '',
    opening_hours: [],
    temporary_closure: { active: false, message: '', start_date: '', end_date: '' },
    account_manager_name: '',
    account_manager_email: '',
    account_manager_phone: '',
    account_manager_photo: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchIdentity = useCallback(async () => {
    if (!currentStore) return;
    try {
      const res = await axios.get(`${API_URL}/api/stores/${currentStore.id}/identity`, { withCredentials: true });
      setIdentity(prev => ({ ...prev, ...res.data }));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [currentStore]);

  useEffect(() => { fetchIdentity(); }, [fetchIdentity]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/stores/${currentStore.id}/identity`, identity, { withCredentials: true });
      toast.success('Identité visuelle sauvegardée');
    } catch (e) {
      toast.error('Erreur');
    } finally { setSaving(false); }
  };

  const update = (key, val) => setIdentity(prev => ({ ...prev, [key]: val }));

  if (!currentStore) return <div className="text-center py-12 text-slate-500">Sélectionnez une boutique</div>;

  return (
    <div className="space-y-6" data-testid="identity-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Identité visuelle</h1>
          <p className="text-sm text-slate-500 mt-1">Couleurs, typographies, logos et favicon de votre boutique</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#0052FF] hover:bg-[#0040CC]" data-testid="save-identity-btn">
          {saving ? <CircleNotch className="animate-spin mr-2" size={16} /> : <FloppyDisk className="mr-2" size={16} />}
          Sauvegarder
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12"><CircleNotch className="animate-spin mx-auto" size={24} /></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Colors */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><Palette size={18} />Palette de couleurs</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <ColorInput label="Couleur principale" value={identity.primary_color} onChange={v => update('primary_color', v)} testId="color-primary" />
                <ColorInput label="Couleur secondaire" value={identity.secondary_color} onChange={v => update('secondary_color', v)} testId="color-secondary" />
                <ColorInput label="Couleur d'accent" value={identity.accent_color} onChange={v => update('accent_color', v)} testId="color-accent" />
                <ColorInput label="Couleur du texte" value={identity.text_color} onChange={v => update('text_color', v)} testId="color-text" />
                <ColorInput label="Couleur de fond" value={identity.background_color} onChange={v => update('background_color', v)} testId="color-background" />
              </div>

              {/* Preview strip */}
              <div className="mt-6 rounded-lg overflow-hidden border">
                <div className="h-3 flex">
                  <div style={{ backgroundColor: identity.primary_color }} className="flex-1" />
                  <div style={{ backgroundColor: identity.secondary_color }} className="flex-1" />
                  <div style={{ backgroundColor: identity.accent_color }} className="flex-1" />
                  <div style={{ backgroundColor: identity.text_color }} className="flex-1" />
                  <div style={{ backgroundColor: identity.background_color }} className="flex-1 border-l" />
                </div>
                <div className="p-4" style={{ backgroundColor: identity.background_color }}>
                  <h3 className="font-bold" style={{ color: identity.text_color, fontFamily: identity.heading_font }}>
                    Aperçu de la palette
                  </h3>
                  <p className="text-sm mt-1" style={{ color: identity.text_color, fontFamily: identity.body_font, opacity: 0.7 }}>
                    Votre boutique utilisera ces couleurs
                  </p>
                  <button className="mt-2 px-4 py-1.5 rounded-lg text-sm text-white" style={{ backgroundColor: identity.primary_color }}>
                    Bouton principal
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Typography */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><TextT size={18} />Typographies</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs">Police des titres</Label>
                <Input value={identity.heading_font} onChange={e => update('heading_font', e.target.value)} placeholder="Inter" data-testid="heading-font" />
              </div>
              <div>
                <Label className="text-xs">Police du corps</Label>
                <Input value={identity.body_font} onChange={e => update('body_font', e.target.value)} placeholder="Inter" data-testid="body-font" />
              </div>
              <div>
                <Label className="text-xs">URL Google Fonts</Label>
                <Input value={identity.google_fonts_url || ''} onChange={e => update('google_fonts_url', e.target.value)} placeholder="https://fonts.googleapis.com/css2?family=..." className="text-xs" />
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500 mb-2">Aperçu typos</p>
                <h4 style={{ fontFamily: identity.heading_font }} className="font-bold text-lg">Titre principal</h4>
                <p style={{ fontFamily: identity.body_font }} className="text-sm text-slate-600">Ceci est un texte de corps avec la police configurée.</p>
              </div>
            </CardContent>
          </Card>

          {/* Logos */}
          <Card className="lg:col-span-3">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><Image size={18} />Logos et icônes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <ImageUpload label="Logo fond clair" value={identity.logo_light} onChange={v => update('logo_light', v)} testId="upload-logo-light" />
                <ImageUpload label="Logo fond sombre" value={identity.logo_dark} onChange={v => update('logo_dark', v)} testId="upload-logo-dark" />
                <ImageUpload label="Favicon" value={identity.favicon} onChange={v => update('favicon', v)} testId="upload-favicon" />
                <ImageUpload label="Icône du site" value={identity.site_icon} onChange={v => update('site_icon', v)} testId="upload-site-icon" />
              </div>
            </CardContent>
          </Card>

          {/* Hero & Contenu */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><Eye size={18} />Contenu Hero & Vidéos</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">ID Vidéo Hero YouTube</Label>
                  <Input value={identity.hero_video_id || ''} onChange={e => update('hero_video_id', e.target.value)} placeholder="sIqthsevzNA" data-testid="hero-video-id" />
                  <p className="text-[10px] text-slate-400 mt-1">Ex: sIqthsevzNA (partie après v= dans l'URL)</p>
                </div>
                <div>
                  <Label className="text-xs">ID Vidéo Boutique YouTube</Label>
                  <Input value={identity.shop_video_id || ''} onChange={e => update('shop_video_id', e.target.value)} placeholder="dVM75L__88s" data-testid="shop-video-id" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Titre Hero — Ligne 1</Label>
                <Input value={identity.hero_line1 || ''} onChange={e => update('hero_line1', e.target.value)} placeholder="Une offre gourmande" data-testid="hero-line1" />
              </div>
              <div>
                <Label className="text-xs">Titre Hero — Ligne 2</Label>
                <Input value={identity.hero_line2 || ''} onChange={e => update('hero_line2', e.target.value)} placeholder="Et d'exception !" data-testid="hero-line2" />
              </div>
              <div>
                <Label className="text-xs">Sous-titre Hero</Label>
                <Input value={identity.hero_subtitle || ''} onChange={e => update('hero_subtitle', e.target.value)} placeholder="Viandes d'exception, sélectionnées avec passion..." data-testid="hero-subtitle" />
              </div>
              <div>
                <Label className="text-xs">Texte bouton CTA Hero</Label>
                <Input value={identity.hero_cta_text || ''} onChange={e => update('hero_cta_text', e.target.value)} placeholder="Découvrir la boutique" data-testid="hero-cta-text" />
              </div>
            </CardContent>
          </Card>

          {/* Contact & Social */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><Palette size={18} />Contact & Réseaux</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs">Nom société (footer)</Label>
                <Input value={identity.company_name || ''} onChange={e => update('company_name', e.target.value)} placeholder="SARL MAGERE" data-testid="company-name" />
              </div>
              <div>
                <Label className="text-xs">Téléphone</Label>
                <Input value={identity.contact_phone || ''} onChange={e => update('contact_phone', e.target.value)} placeholder="04 90 17 42 67" data-testid="contact-phone" />
              </div>
              <div>
                <Label className="text-xs">Email de contact</Label>
                <Input value={identity.contact_email || ''} onChange={e => update('contact_email', e.target.value)} placeholder="contact@example.com" data-testid="contact-email" />
              </div>
              <div>
                <Label className="text-xs">Facebook (URL)</Label>
                <Input value={identity.social_facebook || ''} onChange={e => update('social_facebook', e.target.value)} placeholder="https://facebook.com/..." data-testid="social-facebook" />
              </div>
              <div>
                <Label className="text-xs">Instagram (URL)</Label>
                <Input value={identity.social_instagram || ''} onChange={e => update('social_instagram', e.target.value)} placeholder="https://instagram.com/..." data-testid="social-instagram" />
              </div>
              <div>
                <Label className="text-xs">TikTok (URL)</Label>
                <Input value={identity.social_tiktok || ''} onChange={e => update('social_tiktok', e.target.value)} placeholder="https://tiktok.com/..." data-testid="social-tiktok" />
              </div>
              <div>
                <Label className="text-xs">Google Maps (URL avis)</Label>
                <Input value={identity.google_maps_url || ''} onChange={e => update('google_maps_url', e.target.value)} placeholder="https://google.com/maps/..." data-testid="google-maps-url" />
              </div>
              <div>
                <Label className="text-xs">SIRET</Label>
                <Input value={identity.siret || ''} onChange={e => update('siret', e.target.value)} placeholder="123 456 789 00012" data-testid="siret" />
              </div>
              <div>
                <Label className="text-xs">N° TVA</Label>
                <Input value={identity.tva_number || ''} onChange={e => update('tva_number', e.target.value)} placeholder="FR12345678900" data-testid="tva-number" />
              </div>
              <div>
                <Label className="text-xs">Texte footer</Label>
                <Input value={identity.footer_text || ''} onChange={e => update('footer_text', e.target.value)} placeholder="Ouvert aux particuliers et aux professionnels" data-testid="footer-text" />
              </div>
            </CardContent>
          </Card>

          {/* Account Manager section removed - now managed via /admin/commerciaux */}
        </div>
      )}
    </div>
  );
};

export default IdentityPage;
